import {parseISO} from 'date-fns';
import {isFeatureEnabled} from 'feature-flags';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {CorrectiveTaxDocumentV4RequestBody, useGetTenantQuery} from '@dms/api/core';
import {
  useGetDocumentSeriesDefinitionListQuery,
  useInvoicingDocumentRecalculateMutation,
} from '@dms/api/finance';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {useQueryState, useRequiredParams} from 'shared/web';

import {PERCENTAGE_SIGN} from '../../../constants';
import {useAccountingCalculationCache} from '../../../context/useAccountingCalculationCache';
import {AccountingDocumentFormValues} from '../../../types';
import {createCorrectiveTaxDocumentSchema} from '../../../utils/createCorrectiveTaxDocumentSchema';
import {getInvoicingDocumentType} from '../utils/getInvoicingDocumentType';

export const useGetCorrectiveTaxDocumentBody = () => {
  const [invoiceType] = useQueryState('invoiceType');
  const formatDateTime = useDateTimeFormatter();
  const {id} = useRequiredParams();
  const {data: tenant} = useGetTenantQuery();
  const currency = tenant?.currency as string;
  const {accountingDocumentCalculationCacheKey} = useAccountingCalculationCache();

  const invoicingDocumentType = getInvoicingDocumentType(invoiceType);
  const [_, {data: summary}] = useInvoicingDocumentRecalculateMutation({
    fixedCacheKey: accountingDocumentCalculationCacheKey,
  });

  const {data: documentSeries} = useGetDocumentSeriesDefinitionListQuery();

  const getRequestBody = (
    formData: AccountingDocumentFormValues
  ): CorrectiveTaxDocumentV4RequestBody => {
    const parseResult = createCorrectiveTaxDocumentSchema.safeParse(formData);
    if (parseResult.error) {
      // Typecheck
      throw new Error('It does not match schema');
    }
    const parsedData = parseResult.data;

    const documentSeriesId =
      documentSeries?.find((item) => item.id === parsedData.documentSeriesId)?.documentSeriesId ??
      parsedData.documentSeriesId;

    const items = parsedData.invoiceItems.map((item) => {
      const discount =
        summary?.items.find(
          (summaryItem) => summaryItem.itemId === item.itemId && summaryItem.type === 'discount'
        )?.discount ?? null;

      return {
        currency,
        description: item.description,
        isUnitPriceWithVat: item.isUnitPriceWithVat,
        itemId: item.itemId,
        pricePerUnit: item.pricePerUnit?.toString() ?? '0',
        quantity: item.unit === PERCENTAGE_SIGN ? '1' : (item.quantity?.toString() ?? '0'),
        type: item.type,
        unit: item.unit,
        correctedItemId: item.itemId,
        vatType: item.vatType,
        discount: item.type === 'discount' ? discount : null,
        relatedItemId: item.relatedItemId,
      };
    });

    return {
      dateOfReception: parsedData.dateOfReception ?? '',
      behindItemsNote: parsedData.footerNote ?? null,
      customFields: isNilOrEmpty(parsedData.customFieldsPayload)
        ? undefined
        : parsedData.customFieldsPayload,
      dateOfTaxableSupply: parsedData.dateOfTaxableSupply as string,
      description: i18n.t('entity.correctiveTaxDocument.labels.descriptionContent', {
        invoiceNumber: parsedData.number,
        issueDate: formatDateTime('dateShort', parseISO(parsedData.issueDate)),
      }),
      documentSeriesId,
      dueDate: parsedData.dueDate,
      internalNote: parsedData.internalNote ?? null,
      issueDate: parsedData.issueDate,
      paymentInfo: parsedData.paymentInfo,
      additionalCustomer:
        isNotNil(parsedData.additionalCustomer?.customer.id) &&
        isFeatureEnabled(featureFlags.ACCOUNTING_ADDITIONAL_CUSTOMER) &&
        isNotNil(parsedData.additionalCustomer.contractInformationId)
          ? {
              contractInformationId: parsedData.additionalCustomer.contractInformationId,
              customerId: parsedData.additionalCustomer.customer.id,
              type: parsedData.additionalCustomer.type,
            }
          : null,
      reason: parsedData.reason ?? '',
      invoicingDocumentType,
      invoicingDocumentId: id,
      items,
      invoiceCategory: parsedData.isInternal ? 'internal' : null,
      exchangeRateRatio: parsedData.isExchangeRateRatioEnabled
        ? {
            amount: parseFloat(parsedData.exchangeRateRatio?.amount ?? ''),
            currency: parsedData.exchangeRateRatio?.currency ?? '',
            ratio: parsedData.exchangeRateRatio?.ratio?.toString() ?? '',
          }
        : null,
      documentSource: {
        documentSource: 'internal',
        moduleSource: 'accounting',
        moduleSourceId: null,
        serviceSource: 'omnetic',
      },
    };
  };

  return [getRequestBody];
};
